import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import SqrtInput from "../../../../components/inputs/SqrtInput";

// ==== GENERATOR ====

export function generateTask() {
  const a = Math.floor(Math.random() * 9) + 1;

  const nonReducible = [2, 3, 5, 6, 7, 10, 11, 13, 17, 19];
  const b = nonReducible[Math.floor(Math.random() * nonReducible.length)];

  const aStr = `${a}`;
  const sqrtStr = `\\sqrt{${b}}`;

  const sqrtFirst = Math.random() < 0.5;
  const terms = sqrtFirst ? [sqrtStr, aStr] : [aStr, sqrtStr];

  const expression = `(${terms[0]} + ${terms[1]})^2`;

  const aSquared = a * a;
  const twoab = 2 * a;
  const reduced = aSquared + b;

  const root = `\\sqrt{${b}}`;
  const middle = twoab === 1 ? root : `${twoab}${root}`;

  const expected = `${reduced} + ${middle}`;

  return {
    type: "SquareOfSumTask",
    data: { a, b, expression, expected, sqrtFirst },
  };
}

// ==== RENDER TASK ====

export function renderTask({ data }) {
  return (
    <p>
      Oblicz: <InlineMath math={data.expression} />
    </p>
  );
}

// ==== INPUT ====
// ✅ bez ręcznego "Podgląd:", bo SqrtInput już go wyświetla (ten pogrubiony/spójny)
export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <SqrtInput
      value={userAnswer || ""}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

// ==== CHECK ====

export function checkAnswer({ data }, userAnswer) {
  try {
    const normalize = (str) =>
      String(str ?? "")
        .replace(/\\\\sqrt|\\sqrt|√/g, "sqrt")
        .replace(/\s+/g, "")
        .replace(/\*/g, "")
        .replace(/−/g, "-")
        .toLowerCase()
        .match(/(sqrt\d+|\d+[a-z]+|[a-z]+\^\d+|\d+|[a-z]+)/g)
        ?.sort()
        ?.join("+") || "";

    const expectedStr = data.expected;
    const userStr =
      typeof userAnswer === "string" ? userAnswer : userAnswer?.raw || "";

    return {
      isCorrect: normalize(userStr) === normalize(expectedStr),
      expected: data.expected,
    };
  } catch (e) {
    console.error("Błąd w checkAnswer:", e);
    return { isCorrect: false, expected: data.expected };
  }
}

// ==== ANSWER ====

export function renderAnswer({ data }) {
  return <InlineMath math={`${data.expected}`} />;
}

// ==== EXPLANATION ====

export function renderExplanation({ data }) {
  const { a, b, expression, sqrtFirst } = data;

  const aSquared = a * a;
  const middleCoeff = 2 * a;
  const reduced = aSquared + b;
  const middle = `${middleCoeff}\\sqrt{${b}}`;

  // ✅ kolejność kwadratów zgodna z expression:
  // (√b + a)^2 -> b + 2a√b + a^2
  // (a + √b)^2 -> a^2 + 2a√b + b
  const firstSq = sqrtFirst ? `${b}` : `${aSquared}`;
  const lastSq = sqrtFirst ? `${aSquared}` : `${b}`;

  const computeLine = `${expression} = ${firstSq} + ${middle} + ${lastSq} = ${reduced} + ${middle}`;

  return (
    <div className="space-y-2">
      <p>
        <strong>Krok 1:</strong> Korzystamy ze wzoru skróconego mnożenia:
      </p>
      <BlockMath math="(a + b)^2 = a^2 + 2ab + b^2" />

      <p>
        <strong>Krok 2:</strong> Podstawiamy i upraszczamy:
      </p>
      <BlockMath math={computeLine} />
    </div>
  );
}

export function renderUserAnswer(...args) {
  const ua =
    args.find(
      (x) => !(x && typeof x === "object" && "type" in x && "data" in x)
    ) ?? args[0];
  const raw = typeof ua === "string" ? ua : ua?.raw;
  if (!raw || String(raw).trim() === "") {
    return <i>(brak odpowiedzi)</i>;
  }
  return <InlineMath math={raw} />;
}
